// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — FlowEditor (M3: V·IANet Flow)
// Compositor: hooks + canvas + panels. Prompt 138 refactor.
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useI18n } from '../../../../../i18n';
import { request } from '../../../../shared/services/api';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import {
  ReactFlowProvider,
  useReactFlow,
  applyNodeChanges,
  applyEdgeChanges,
  type OnNodesChange,
  type OnEdgesChange,
} from '@xyflow/react';

import type { FlowNode, FlowEdge, FlowNodeData, Swimlane } from '../types/flowchart.types';

import { FLOW_TOKENS as T } from '../shared/flowTokens';
import { useFlowData, normalizeEdges } from '../hooks/useFlowData';
import { useCommandStack } from '../hooks/useCommandStack';
import { useAutoLayout } from '../hooks/useAutoLayout';
import { useFlowExport } from '../hooks/useFlowExport';
import { useFlowKeyboard } from '../hooks/useFlowKeyboard';
import { useInlineEdit } from '../hooks/useInlineEdit';
import { useFlowPhases } from '../hooks/useFlowPhases';
import { useFullscreen } from '../hooks/useFullscreen';
import { alignNodes, distributeNodes } from '../utils/alignment';
import { applyEdgeProperties } from '../utils/edge-properties';

import FlowToolbar from './FlowToolbar';
import ShapeLibrary from './ShapeLibrary';
import FlowCanvas from './FlowCanvas';
import PropertiesPanel, { type EdgeProperties } from './PropertiesPanel';
import VersionTimeline from './VersionTimeline';
import AuditTrail from './AuditTrail';
import FlowDiff from '../viewer/FlowDiff';
import NodeDetailPanel from '../viewer/NodeDetailPanel';
import { FlowHeader } from './FlowHeader';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import type { EstadoDocumento } from '../../../../../types';
import type { ContextMenuAction, ContextMenuType } from './ContextMenu';

// ─── Handle CSS (injected once) ─────────────────────────────────────────────

const HANDLE_STYLES = `
.react-flow__handle {
  width: 6px !important; height: 6px !important; border-radius: 50% !important;
  background: #FFFFFF !important; border: 1px solid #B0B8C4 !important;
  opacity: 0; transition: opacity 0.15s;
}
.react-flow__node:hover .react-flow__handle,
.react-flow__node.selected .react-flow__handle { opacity: 1; }
.react-flow__handle:hover { border-color: #33BCE1 !important; background: #33BCE1 !important; }
`;

// ─── Exposed state for parent absorption ─────────────────────────────────────

export interface FlujogramaState {
  flujograma: import('../types/flowchart.types').Flujograma | null;
  currentEstado: import('../../../../../types').EstadoDocumento;
  versions: import('../hooks/useFlowData').FlowVersion[];
  selectedVersionIndex: number;
  editing: boolean;
  saving: boolean;
  personName: string | null;
  isFullscreen: boolean;
  onSelectVersion: (index: number) => void;
  onEnterEdit: () => void;
  onSave: () => void;
  onExitEdit: () => void;
  onDiscard: () => void;
  onNuevaEdicion: () => void;
  onToggleFullscreen: () => void;
  onDeleteDraft?: () => void;
  onArchive?: () => void;
}

// ─── Inner Editor (needs ReactFlowProvider) ──────────────────────────────────

function FlowEditorInner({
  procesoId,
  procesoCodigo,
  procesoNombre,
  onViewModeChange,
  onFlujogramaState,
}: {
  procesoId: string;
  procesoCodigo?: string;
  procesoNombre?: string;
  onViewModeChange?: (mode: 'ficha' | 'docs' | 'flow') => void;
  onFlujogramaState?: (state: FlujogramaState) => void;
}) {
  const { t } = useI18n();
  const { zoomIn, zoomOut, setViewport, getZoom: _getZoom } = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);

  // ─── Core state ───────────────────────────────────────────────────────────
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [edges, setEdges] = useState<FlowEdge[]>([]);
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [selectedNodePos, setSelectedNodePos] = useState<{ x: number; y: number } | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<FlowEdge | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [showMinimap, setShowMinimap] = useState(false);
  const [saving, setSaving] = useState(false);
  // 0661.1: showSizeWarning eliminado — banner "30 nodos max" no normativo.
  const [lanes, setLanes] = useState<Swimlane[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [editing, setEditing] = useState(false);
  const [resizeEnabled, setResizeEnabled] = useState(false);
  const [diffState, setDiffState] = useState<{ v1: number; v2: number } | null>(null);
  const [_mousePos, _setMousePos] = useState<{ x: number; y: number } | null>(null);
  const [nodeCard, setNodeCard] = useState<{
    node: FlowNode;
    initialPos: { x: number; y: number };
  } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'default' | 'danger';
  }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const positionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Domain hooks ─────────────────────────────────────────────────────────
  const {
    flujograma,
    loading,
    error,
    versions,
    selectedVersionIndex,
    guardarBorrador,
    crearFlujograma,
    enviarRevision: _enviarRevision,
    selectVersion,
    nuevaEdicion,
    recargar,
  } = useFlowData(procesoId);

  const { pushState, undo, redo, canUndo, canRedo } = useCommandStack();
  const { applyLayout, isLayouting } = useAutoLayout();
  const {
    phases,
    setPhases,
    handleAddPhase,
    handleRemovePhase,
    handleRenamePhase,
    handleResizePhase,
  } = useFlowPhases();
  const { isFullscreen, toggleFullscreen } = useFullscreen({ containerRef });
  const { handleExportPng, handleExportPdf, handleExportSvg } = useFlowExport({
    containerRef,
    flujograma,
    procesoId,
  });

  const {
    inlineEdgeEdit,
    setInlineEdgeEdit,
    handleNodeDoubleClick,
    handleEdgeDoubleClick,
    handleInlineEdgeEditSave,
  } = useInlineEdit({ nodes, edges, pushState, setNodes, setEdges, containerRef });

  // ─── Toast helper ─────────────────────────────────────────────────────────
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // ─── Person name ──────────────────────────────────────────────────────────
  const [personName, setPersonName] = useState<string | null>(null);
  useEffect(() => {
    if (!flujograma?.creadoPor) {
      setPersonName(null);
      return;
    }
    request<{ data: { nombre?: string; apellidos?: string }[] }>(
      `/personas?userId=${encodeURIComponent(flujograma.creadoPor)}&porPagina=1`,
    )
      .then((body) => {
        const p = body?.data?.[0];
        if (p) setPersonName([p.nombre, p.apellidos].filter(Boolean).join(' '));
      })
      .catch(() => {});
  }, [flujograma?.creadoPor]);

  // ─── Workflow transitions ─────────────────────────────────────────────────
  // Las transiciones legales del flujograma las gestiona <FlujogramaWorkflowBar>
  // (renderizado dentro de FlowHeader) usando el motor unificado /transitar.
  const currentEstado = (versions[selectedVersionIndex]?.estado ??
    flujograma?.estado ??
    'BORRADOR') as EstadoDocumento;

  // ─── Fit to Canvas (A3) — pegado arriba+izq+der ────────────────────────
  const fitToCanvas = useCallback(() => {
    const el = document.querySelector('.react-flow');
    if (!el) return;
    const zoom = el.clientWidth / T.canvas.a3Width;
    setViewport({ x: 0, y: 20, zoom }, { duration: 200 });
  }, [setViewport]);

  const selectedNodeIds = useMemo(() => nodes.filter((n) => n.selected).map((n) => n.id), [nodes]);

  // ─── Load flowchart data ─────────────────────────────────────────────────
  useEffect(() => {
    if (!flujograma?.contenido) return;
    const { nodes: loadedNodes, edges: loadedEdges } = flujograma.contenido;
    const nodesWithDefaults = (loadedNodes ?? []).map((n: FlowNode) => {
      const isSmall =
        n.type === 'DECISION' ||
        n.type === 'PROCESO_REF' ||
        n.type === 'decision' ||
        n.type === 'proceso_ref';
      return {
        ...n,
        style: n.style || {
          width: isSmall ? T.node.decisionSize : T.node.width,
          height: isSmall ? T.node.decisionSize : T.node.height,
        },
      };
    });
    setNodes(nodesWithDefaults);
    setEdges(normalizeEdges(loadedEdges ?? []));
    if (flujograma.contenido.metadata?.swimlanes)
      setLanes(flujograma.contenido.metadata.swimlanes as Swimlane[]);
    if (
      flujograma.contenido.metadata?.phases &&
      (flujograma.contenido.metadata.phases as import('../types/flowchart.types').FlowPhase[])
        .length > 0
    ) {
      setPhases(
        flujograma.contenido.metadata.phases as import('../types/flowchart.types').FlowPhase[],
      );
    } else {
      const CW = T.canvas.a3Width;
      const pw = Math.floor(CW / 4);
      setPhases([
        { id: 'p1', name: 'Fase 1', width: pw },
        { id: 'p2', name: 'Fase 2', width: pw },
        { id: 'p3', name: 'Fase 3', width: pw },
        { id: 'p4', name: 'Fase 4', width: CW - pw * 3 },
      ]);
    }
    setTimeout(() => fitToCanvas(), 200);
  }, [flujograma, setViewport]);

  // ─── Propagate resizeEnabled ──────────────────────────────────────────────
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => ({ ...n, data: { ...n.data, resizeEnabled } as FlowNodeData })),
    );
  }, [resizeEnabled]);

  // 0661.1: useEffect(setShowSizeWarning) eliminado — banner no normativo.

  // ─── Node/Edge change handlers ────────────────────────────────────────────
  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      const hasPositionChange = changes.some((c) => c.type === 'position' && c.dragging === false);
      setNodes((nds) => applyNodeChanges(changes, nds) as FlowNode[]);
      if (hasPositionChange) {
        if (positionTimerRef.current) clearTimeout(positionTimerRef.current);
        positionTimerRef.current = setTimeout(() => pushState({ nodes, edges }), 300);
      }
      if (changes.some((c) => c.type === 'add' || c.type === 'remove')) pushState({ nodes, edges });
    },
    [pushState, nodes, edges],
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      if (changes.some((c) => c.type === 'add' || c.type === 'remove')) pushState({ nodes, edges });
      setEdges((eds) => applyEdgeChanges(changes, eds) as FlowEdge[]);
    },
    [pushState, nodes, edges],
  );

  // ─── Action handlers ──────────────────────────────────────────────────────
  const handleUndo = useCallback(() => {
    const s = undo({ nodes, edges });
    if (s) {
      setNodes(s.nodes);
      setEdges(s.edges);
    }
  }, [undo, nodes, edges]);
  const handleRedo = useCallback(() => {
    const s = redo({ nodes, edges });
    if (s) {
      setNodes(s.nodes);
      setEdges(s.edges);
    }
  }, [redo, nodes, edges]);
  const handleAutoLayout = useCallback(async () => {
    pushState({ nodes, edges });
    await applyLayout(nodes, edges, setNodes);
  }, [pushState, applyLayout, nodes, edges, setNodes]);
  const handleAlign = useCallback(
    (mode: import('./FlowToolbar').AlignMode) => {
      if (selectedNodeIds.length < 2) return;
      pushState({ nodes, edges });
      setNodes((nds) => alignNodes(nds, selectedNodeIds, mode));
    },
    [selectedNodeIds, pushState, nodes, edges],
  );
  const handleDistribute = useCallback(
    (axis: import('./FlowToolbar').DistributeAxis) => {
      if (selectedNodeIds.length < 3) return;
      pushState({ nodes, edges });
      setNodes((nds) => distributeNodes(nds, selectedNodeIds, axis));
    },
    [selectedNodeIds, pushState, nodes, edges],
  );
  const handleMatchSize = useCallback(() => {
    if (selectedNodeIds.length < 2) return;
    const ref = nodes.find((n) => n.id === selectedNodeIds[0]);
    if (!ref?.style) return;
    const w = ref.style.width as number | undefined;
    const h = ref.style.height as number | undefined;
    if (!w || !h) return;
    pushState({ nodes, edges });
    const ids = new Set(selectedNodeIds.slice(1));
    setNodes((nds) =>
      nds.map((n) => (ids.has(n.id) ? { ...n, style: { ...n.style, width: w, height: h } } : n)),
    );
  }, [selectedNodeIds, nodes, edges, pushState]);
  const handleCollapseAll = useCallback(() => {
    setNodes((nds) =>
      nds.map((n) =>
        (n.data as FlowNodeData).nodeType === 'SUBPROCESO'
          ? { ...n, data: { ...n.data, expanded: false } as FlowNodeData }
          : n,
      ),
    );
  }, []);

  const handleDuplicate = useCallback(() => {
    const selected = nodes.filter((n) => n.selected);
    if (selected.length === 0) return;
    pushState({ nodes, edges });
    const newNodes = selected.map((n) => {
      const isSmall =
        (n.data as FlowNodeData).nodeType === 'DECISION' ||
        (n.data as FlowNodeData).nodeType === 'PROCESO_REF';
      return {
        ...n,
        id: `node_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        position: { x: n.position.x + 20, y: n.position.y + 20 },
        selected: true,
        data: { ...n.data } as FlowNodeData,
        style: n.style || {
          width: isSmall ? T.node.decisionSize : T.node.width,
          height: isSmall ? T.node.decisionSize : T.node.height,
        },
      };
    });
    setNodes((nds) => [
      ...nds.map((n) => (n.selected ? { ...n, selected: false } : n)),
      ...newNodes,
    ]);
  }, [nodes, edges, pushState]);

  const handleUpdateNode = useCallback(
    (nodeId: string, data: Partial<FlowNodeData>) => {
      pushState({ nodes, edges });
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...data } as FlowNodeData } : n,
        ),
      );
      setSelectedNode((prev) =>
        prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...data } as FlowNodeData } : prev,
      );
    },
    [pushState, nodes, edges],
  );

  const handleUpdateEdge = useCallback(
    (edgeId: string, props: EdgeProperties) => {
      pushState({ nodes, edges });
      setEdges((eds) => eds.map((e) => (e.id === edgeId ? applyEdgeProperties(e, props) : e)));
      setSelectedEdge((prev) => (prev?.id === edgeId ? applyEdgeProperties(prev, props) : prev));
    },
    [pushState, nodes, edges],
  );

  // ─── Save / Edit mode ─────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const viewport = { x: 0, y: 0, zoom: 1 };
      const metadata: Record<string, unknown> = { phases, swimlanes: lanes };
      if (flujograma) await guardarBorrador(nodes, edges, viewport, undefined, metadata);
      else await crearFlujograma(nodes, edges, viewport, metadata);
    } catch (err: any) {
      showToast(err.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [flujograma, nodes, edges, phases, lanes, guardarBorrador, crearFlujograma, showToast]);

  const handleEnterEditMode = useCallback(() => {
    if (currentEstado === 'BORRADOR') setEditing(true);
  }, [currentEstado]);
  const handleExitEditMode = useCallback(() => {
    setEditing(false);
  }, []);
  const handleDiscardChanges = useCallback(() => {
    if (flujograma?.contenido) {
      const { nodes: ln, edges: le } = flujograma.contenido;
      const nwd = (ln ?? []).map((n: FlowNode) => {
        const s =
          n.type === 'DECISION' ||
          n.type === 'PROCESO_REF' ||
          n.type === 'decision' ||
          n.type === 'proceso_ref';
        return {
          ...n,
          style: n.style || {
            width: s ? T.node.decisionSize : T.node.width,
            height: s ? T.node.decisionSize : T.node.height,
          },
        };
      });
      setNodes(nwd);
      setEdges(normalizeEdges(le ?? []));
    }
    setEditing(false);
  }, [flujograma]);
  const handleSelectVersion = useCallback(
    async (index: number) => {
      setEditing(false);
      await selectVersion(index);
    },
    [selectVersion],
  );
  const handleNuevaEdicion = useCallback(async () => {
    try {
      await nuevaEdicion();
      setEditing(true);
      showToast(t.flow.nuevaEdicionCreada ?? 'Nueva edición creada');
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : (t.flow.errorNuevaEdicion ?? 'Error al crear nueva edición');
      showToast(msg);
    }
  }, [nuevaEdicion, showToast, t]);

  const handleDeleteDraft = useCallback(() => {
    if (!flujograma) return;
    setConfirmDialog({
      open: true,
      title: 'Eliminar borrador',
      variant: 'danger',
      message: 'Se eliminará este borrador de forma permanente. ¿Continuar?',
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        try {
          await request(`/flujogramas/edit/${flujograma.id}`, { method: 'DELETE' });
          await recargar();
        } catch (err: unknown) {
          const msg =
            err instanceof Error
              ? err.message
              : (t.flow.errorEliminar ?? 'Error al eliminar borrador');
          showToast(msg);
        }
      },
    });
  }, [flujograma, recargar, showToast, t]);

  const handleArchive = useCallback(() => {
    if (!flujograma) return;
    setConfirmDialog({
      open: true,
      title: 'Archivar flujograma',
      message: 'Se archivará este flujograma. Ya no estará vigente. ¿Continuar?',
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        try {
          await request(`/flujogramas/edit/${flujograma.id}/archive`, { method: 'POST' });
          await recargar();
        } catch (err: unknown) {
          const msg =
            err instanceof Error ? err.message : (t.flow.errorArchivar ?? 'Error al archivar');
          showToast(msg);
        }
      },
    });
  }, [flujograma, recargar, showToast, t]);

  // ─── Emit state to parent (when embedded) ─────────────────────────────────
  useEffect(() => {
    if (!onFlujogramaState) return;
    onFlujogramaState({
      flujograma,
      currentEstado,
      versions,
      selectedVersionIndex,
      editing,
      saving,
      personName,
      isFullscreen,
      onSelectVersion: handleSelectVersion,
      onEnterEdit: handleEnterEditMode,
      onSave: handleSave,
      onExitEdit: handleExitEditMode,
      onDiscard: handleDiscardChanges,
      onNuevaEdicion: handleNuevaEdicion,
      onToggleFullscreen: toggleFullscreen,
      onDeleteDraft: handleDeleteDraft,
      onArchive: handleArchive,
    });
  }, [
    flujograma,
    currentEstado,
    versions,
    selectedVersionIndex,
    editing,
    saving,
    personName,
    isFullscreen,
    onFlujogramaState,
    handleSelectVersion,
    handleEnterEditMode,
    handleSave,
    handleExitEditMode,
    handleDiscardChanges,
    handleNuevaEdicion,
    toggleFullscreen,
    handleDeleteDraft,
    handleArchive,
  ]);

  // ─── Context menu ─────────────────────────────────────────────────────────
  const handleContextAction = useCallback(
    (action: ContextMenuAction, targetId?: string, targetType?: ContextMenuType) => {
      switch (action) {
        case 'duplicate':
          if (targetId) {
            const node = nodes.find((n) => n.id === targetId);
            if (node) {
              pushState({ nodes, edges });
              const s =
                (node.data as FlowNodeData).nodeType === 'DECISION' ||
                (node.data as FlowNodeData).nodeType === 'PROCESO_REF';
              const nn: FlowNode = {
                ...node,
                id: `node_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                position: { x: node.position.x + 20, y: node.position.y + 20 },
                selected: true,
                data: { ...node.data } as FlowNodeData,
                style: node.style || {
                  width: s ? T.node.decisionSize : T.node.width,
                  height: s ? T.node.decisionSize : T.node.height,
                },
              };
              setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), nn]);
            }
          }
          break;
        case 'bringToFront':
          if (targetId)
            setNodes((nds) => {
              const i = nds.findIndex((n) => n.id === targetId);
              return i < 0 ? nds : [...nds.slice(0, i), ...nds.slice(i + 1), nds[i]];
            });
          break;
        case 'sendToBack':
          if (targetId)
            setNodes((nds) => {
              const i = nds.findIndex((n) => n.id === targetId);
              return i < 0 ? nds : [nds[i], ...nds.slice(0, i), ...nds.slice(i + 1)];
            });
          break;
        case 'delete':
          if (targetType === 'node' && targetId) {
            pushState({ nodes, edges });
            setNodes((nds) => nds.filter((n) => n.id !== targetId));
            setEdges((eds) => eds.filter((e) => e.source !== targetId && e.target !== targetId));
          } else if (targetType === 'edge' && targetId) {
            pushState({ nodes, edges });
            setEdges((eds) => eds.filter((e) => e.id !== targetId));
          }
          break;
        case 'editLabel':
          if (targetId) {
            const edge = edges.find((e) => e.id === targetId);
            if (edge) handleEdgeDoubleClick(edge);
          }
          break;
        case 'changeStyleSolid':
        case 'changeStyleDashed':
        case 'changeStyleDotted':
          if (targetId) {
            const m: Record<string, string> = {
              changeStyleSolid: 'solid',
              changeStyleDashed: 'dashed',
              changeStyleDotted: 'dotted',
            };
            handleUpdateEdge(targetId, { lineStyle: m[action] as 'solid' | 'dashed' | 'dotted' });
          }
          break;
        case 'changeArrowArrow':
        case 'changeArrowNone':
        case 'changeArrowBidirectional':
        case 'changeArrowOpen':
          if (targetId) {
            const m: Record<string, string> = {
              changeArrowArrow: 'arrow',
              changeArrowNone: 'none',
              changeArrowBidirectional: 'bidirectional',
              changeArrowOpen: 'open',
            };
            handleUpdateEdge(targetId, {
              arrowType: m[action] as 'arrow' | 'none' | 'bidirectional' | 'open',
            });
          }
          break;
        case 'fitView':
          fitToCanvas();
          break;
        case 'autoLayout':
          handleAutoLayout();
          break;
        case 'paste':
          break;
      }
    },
    [
      nodes,
      edges,
      pushState,
      fitToCanvas,
      handleAutoLayout,
      handleEdgeDoubleClick,
      handleUpdateEdge,
    ],
  );

  // ─── Keyboard shortcuts ───────────────────────────────────────────────────
  useFlowKeyboard({
    editing,
    inlineEditActive: !!inlineEdgeEdit,
    onUndo: handleUndo,
    onRedo: handleRedo,
    onSave: handleSave,
    onDuplicate: handleDuplicate,
    onSelectAll: () => {
      setNodes((nds) => nds.map((n) => ({ ...n, selected: true })));
      setEdges((eds) => eds.map((e) => ({ ...e, selected: true })));
    },
  });

  // ─── Loading / Error ──────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <LoadingSpinner text={t.flow.cargandoFlujograma} />
      </div>
    );
  if (error)
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-sm text-red-500 mb-2">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm bg-white border border-gray-200 rounded-md hover:bg-gray-50"
          >
            {t.flow.reintentar}
          </button>
        </div>
      </div>
    );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className="h-full flex flex-col"
      style={{ position: 'relative', background: isFullscreen ? '#F8F8F8' : undefined }}
    >
      <style dangerouslySetInnerHTML={{ __html: HANDLE_STYLES }} />
      {/* 0661.1: SizeWarning eliminado — regla "30 nodos max" no normativa. */}

      {/* Barra 1: Unified header — solo cuando NO embebido en panel padre */}
      {!onViewModeChange && (
        <div style={{ position: 'relative', zIndex: 10, flexShrink: 0 }}>
          <FlowHeader
            procesoId={procesoId}
            procesoCodigo={procesoCodigo}
            procesoNombre={procesoNombre}
            flujograma={flujograma}
            editing={editing}
            saving={saving}
            personName={personName}
            onEnterEdit={handleEnterEditMode}
            onSave={handleSave}
            onExitEdit={handleExitEditMode}
            onDiscard={handleDiscardChanges}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
          />
        </div>
      )}

      {/* Barra 2: Toolbar solo en edicion */}
      {editing && (
        <div style={{ position: 'relative', zIndex: 10, flexShrink: 0 }}>
          <FlowToolbar
            onSave={handleSave}
            onVersionHistory={() => {
              setShowVersions((v) => !v);
              setShowAuditTrail(false);
            }}
            onAuditTrail={() => {
              setShowAuditTrail((v) => !v);
              setShowVersions(false);
            }}
            onUndo={handleUndo}
            onRedo={handleRedo}
            canUndo={canUndo}
            canRedo={canRedo}
            onZoomIn={() => zoomIn()}
            onZoomOut={() => zoomOut()}
            onFitView={fitToCanvas}
            onCollapseAll={handleCollapseAll}
            onAutoLayout={handleAutoLayout}
            isLayouting={isLayouting}
            hasNodes={nodes.length > 0}
            onToggleGrid={() => setShowGrid((v) => !v)}
            onToggleMinimap={() => setShowMinimap((v) => !v)}
            onExportPng={handleExportPng}
            onExportPdf={handleExportPdf}
            onExportSvg={handleExportSvg}
            showGrid={showGrid}
            showMinimap={showMinimap}
            saving={saving}
            editing={editing}
            selectedCount={selectedNodeIds.length}
            onAlign={handleAlign}
            onDistribute={handleDistribute}
            onMatchSize={handleMatchSize}
            resizeEnabled={resizeEnabled}
            onToggleResize={() => setResizeEnabled((v) => !v)}
          />
        </div>
      )}

      {/* Body: ShapeLibrary | Canvas | Properties | Panels */}
      <div className="flex-1 flex overflow-hidden" style={{ position: 'relative', zIndex: 1 }}>
        {editing && (
          <ShapeLibrary
            onAddPhase={handleAddPhase}
            phases={phases}
            onRenamePhase={handleRenamePhase}
            onRemovePhase={handleRemovePhase}
            onResizePhase={handleResizePhase}
          />
        )}
        <FlowCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          setNodes={setNodes}
          setEdges={setEdges}
          onNodeSelect={
            editing
              ? (node: FlowNode | null, event?: React.MouseEvent) => {
                  setSelectedNode(node);
                  if (node && event) {
                    try {
                      const container = containerRef.current?.getBoundingClientRect();
                      if (container) {
                        const target = (event.target as HTMLElement).closest('.react-flow__node');
                        const nr = target?.getBoundingClientRect();
                        if (nr) {
                          let x = nr.right - container.left + 12;
                          const y = nr.top - container.top;
                          if (x + 320 > container.width - 8) x = nr.left - container.left - 332;
                          setSelectedNodePos({
                            x,
                            y: Math.max(8, Math.min(y, container.height - 200)),
                          });
                        } else {
                          setSelectedNodePos({
                            x: event.clientX - container.left + 16,
                            y: event.clientY - container.top - 20,
                          });
                        }
                      }
                    } catch {
                      setSelectedNodePos(null);
                    }
                  } else {
                    setSelectedNodePos(null);
                  }
                }
              : undefined
          }
          onEdgeSelect={editing ? setSelectedEdge : undefined}
          onNodeClick={
            !editing
              ? (node: FlowNode, event: React.MouseEvent) =>
                  setNodeCard({
                    node,
                    initialPos: (() => {
                      const panelW = 300;
                      const margin = 80;
                      const spaceRight = window.innerWidth - event.clientX;
                      const x =
                        spaceRight > panelW + margin
                          ? event.clientX + margin
                          : Math.max(20, event.clientX - panelW - margin);
                      const y = Math.min(
                        Math.max(event.clientY - 60, 20),
                        window.innerHeight - 400,
                      );
                      return { x, y };
                    })(),
                  })
              : undefined
          }
          showGrid={showGrid}
          showMinimap={showMinimap}
          lanes={lanes}
          phases={phases}
          onMousePosition={_setMousePos}
          onContextAction={editing ? handleContextAction : undefined}
          onNodeDoubleClick={editing ? handleNodeDoubleClick : undefined}
          onEdgeDoubleClick={editing ? handleEdgeDoubleClick : undefined}
          onRenamePhase={editing ? handleRenamePhase : undefined}
          onRemovePhase={editing ? handleRemovePhase : undefined}
          readOnly={!editing}
          onFitToCanvas={fitToCanvas}
        />

        {/* Card flotante read-only en modo VER — arrastrable */}
        {!editing && nodeCard && (
          <NodeDetailPanel
            node={nodeCard.node}
            onClose={() => setNodeCard(null)}
            initialPosition={nodeCard.initialPos}
            onNavigateDocument={(codigo) => window.open(`/documento/${codigo}`, '_blank')}
            onNavigateProcess={(codigo) => window.open(`/proceso/${codigo}`, '_blank')}
            procesoId={procesoId}
          />
        )}

        {editing && (
          <PropertiesPanel
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            onUpdateNode={handleUpdateNode}
            onUpdateEdge={handleUpdateEdge}
            onClose={() => {
              setSelectedNode(null);
              setSelectedEdge(null);
              setSelectedNodePos(null);
            }}
            nodePosition={selectedNodePos}
            onResizeNode={(nodeId, scale) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === nodeId
                    ? {
                        ...n,
                        style: {
                          ...n.style,
                          width: Math.round(
                            (n.data as FlowNodeData).nodeType === 'PROCESO_REF'
                              ? T.node.refRadius * 2 * scale
                              : (n.data as FlowNodeData).nodeType === 'DECISION'
                                ? T.node.decisionSize * scale
                                : T.node.width * scale,
                          ),
                          height: Math.round(
                            (n.data as FlowNodeData).nodeType === 'PROCESO_REF'
                              ? T.node.refRadius * 2 * scale
                              : (n.data as FlowNodeData).nodeType === 'DECISION'
                                ? T.node.decisionSize * scale
                                : T.node.height * scale,
                          ),
                        },
                      }
                    : n,
                ),
              );
            }}
            fases={phases.map((p) => ({ id: p.id, nombre: p.name }))}
            procesoId={procesoId}
          />
        )}

        {/* Inline edge label edit */}
        {inlineEdgeEdit && (
          <div
            style={{
              position: 'absolute',
              left: inlineEdgeEdit.pos.x,
              top: inlineEdgeEdit.pos.y,
              zIndex: 100,
            }}
          >
            <input
              autoFocus
              value={inlineEdgeEdit.value}
              onChange={(e) => setInlineEdgeEdit({ ...inlineEdgeEdit, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInlineEdgeEditSave();
                if (e.key === 'Escape') setInlineEdgeEdit(null);
              }}
              onBlur={handleInlineEdgeEditSave}
              placeholder="Etiqueta..."
              style={{
                width: 120,
                height: 24,
                border: '2px solid #33BCE1',
                borderRadius: 3,
                backgroundColor: '#FFFFFF',
                textAlign: 'center',
                fontSize: '8px',
                fontWeight: 600,
                fontFamily: T.font.family,
                color: T.color.textPrimary,
                outline: 'none',
                padding: '2px 4px',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Version Timeline */}
        {showVersions && (
          <VersionTimeline
            procesoId={procesoId}
            currentVersion={flujograma?.version}
            onSelectVersion={() => {}}
            onCompare={(v1, v2) => setDiffState({ v1, v2 })}
            onClose={() => setShowVersions(false)}
          />
        )}

        {/* Audit Trail */}
        {showAuditTrail && flujograma?.id && (
          <AuditTrail flowchartId={flujograma.id} onClose={() => setShowAuditTrail(false)} />
        )}
      </div>

      {/* FlowDiff overlay */}
      {diffState && flujograma?.id && (
        <FlowDiff
          flowchartId={flujograma.id}
          procesoId={procesoId}
          versionA={diffState.v1}
          versionB={diffState.v2}
          onClose={() => setDiffState(null)}
        />
      )}

      {/* Confirm Dialog (delete draft / archive) — el de transiciones lo maneja
          <FlujogramaWorkflowBar> internamente vía <ConfirmModal> centralizado. */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((d) => ({ ...d, open: false }))}
        variant={confirmDialog.variant}
      />

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            backgroundColor: '#1F3864',
            color: '#fff',
            padding: '10px 24px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export default function FlowEditor({
  procesoId,
  procesoCodigo,
  procesoNombre,
  onViewModeChange,
  onFlujogramaState,
}: {
  procesoId: string;
  procesoCodigo?: string;
  procesoNombre?: string;
  onViewModeChange?: (mode: 'ficha' | 'docs' | 'flow') => void;
  onFlujogramaState?: (state: FlujogramaState) => void;
}) {
  return (
    <ReactFlowProvider>
      <FlowEditorInner
        procesoId={procesoId}
        procesoCodigo={procesoCodigo}
        procesoNombre={procesoNombre}
        onViewModeChange={onViewModeChange}
        onFlujogramaState={onFlujogramaState}
      />
    </ReactFlowProvider>
  );
}
